import { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip, Collapse } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AgGridReact } from "ag-grid-react";
import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import DashboardLayoutModal from "@plannel/components/dashboard/DashboardLayoutModal";
import { SegToggle } from "@plannel/components/dashboard/DashboardToggle";
import { KpiCard } from "@plannel/components/dashboard/KpiCard";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import { dashboardDefaultColDef, dashboardGridSx, DASHBOARD_ROW_HEIGHT } from "@plannel/components/dashboard/dashboardGrid";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import useDashboardP13n, { MAX_TOP_N } from "@plannel/hooks/useDashboardP13n";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import psiBalanceService from "@plannel/services/dashboard/psi-balance-service";
import reduxUtil from "@plannel/utils/redux-util";
import { FS } from "@plannel/utils/font-util";

/*
 * PSI Balance — 거점 × 품목 불균형 진단 + 재배치 기회.
 * 산식·설계 근거: saas-application/docs/sql/dw/dashboard-spec/07_psi_balance.md
 *
 * 이 화면이 맡는 질문은 "어디가 불균형하고 무엇을 옮기면 풀리는가" 하나다.
 *   PSI 흐름(S/P/I 시계열)은 Location Inventory 가 이미 하므로 여기선 드릴 링크만 건다(명세 §8).
 */

const CARD_DEFS = [
  { key: "matrix", span: 6, minH: 300 },
  { key: "topImbalance", span: 3, minH: 300 },
  { key: "transshipment", span: 3, minH: 300 },
];
const DEFAULT_LAYOUT = { order: CARD_DEFS.map((c) => c.key), hidden: {} };

// 드릴 대상 — PSI 흐름은 Location Inventory 가 보여준다(명세 §8)
const DRILL = { locationInventory: "LOCATION_INVENTORY_STATUS" };

// ─────────────────────────────────────────
// 불균형 매트릭스 — 행=거점, 열=품목, 셀=순편차
// ─────────────────────────────────────────
/*
 * 발산형 색: 과잉(+)은 warning, 부족(−)은 error 로 **방향을 색조로** 구분하고 강도는 alpha 로 준다.
 *   0 은 "목표에 정확히 맞음" 이라 칠하지 않는다. 데이터가 없는 조합은 아예 셀을 비운다
 *   — 0 과 "없음" 이 같아 보이면 안 된다(명세 §2).
 */
const cellBg = (theme, net, max) => {
  if (!net || !max) return "transparent";
  const base = net > 0 ? theme.palette.warning.main : theme.palette.error.main;
  return alpha(base, 0.12 + 0.55 * Math.min(Math.abs(net) / max, 1));
};

const MatrixCard = ({ title, matrix, unit, fmtVal, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const locations = matrix?.locations ?? [];
  const items = matrix?.items ?? [];
  const valKey = unit === "AMT" ? "netAmt" : "net";

  // 셀 조회용 맵 + 강도 기준이 되는 최대 절대값
  //   ★ `matrix?.cells ?? []` 를 밖에서 만들면 매 렌더 새 배열이라 의존성이 계속 바뀐다 → 안에서 읽는다
  const { byKey, max } = useMemo(() => {
    const m = new Map();
    let mx = 0;
    (matrix?.cells ?? []).forEach((c) => {
      m.set(`${c.loc}|${c.item}`, c[valKey]);
      mx = Math.max(mx, Math.abs(c[valKey]));
    });
    return { byKey: m, max: mx };
  }, [matrix, valKey]);

  if (!locations.length || !items.length) {
    return (
      <>
        <DashboardCardHeader title={title} dense {...drill} />
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("noData")}</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>
        {/* 범례 — 색조가 방향, 진하기가 크기라는 것만 알려준다 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: alpha(theme.palette.warning.main, 0.55) }} />
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("excess")}</Typography>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: alpha(theme.palette.error.main, 0.55), ml: 0.5 }} />
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("shortage")}</Typography>
        </Box>
      </DashboardCardHeader>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: `minmax(120px, auto) repeat(${items.length}, minmax(64px, 1fr))`,
          fontSize: FS.CAPTION,
        }}>
          {/* 헤더 행 */}
          <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper" }} />
          {items.map((it) => (
            <Box key={it} sx={{
              position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper",
              px: 0.5, py: 0.75, textAlign: "center", color: "text.secondary",
              borderBottom: 1, borderColor: "divider", whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis",
            }}>{it}</Box>
          ))}
          {/* 본문 — 거점 유형이 바뀌는 자리에 구분선을 넣어 DC 덩어리와 STO 덩어리를 갈라 준다 */}
          {locations.map((row, li) => {
            const newGroup = li > 0 && locations[li - 1]?.type !== row.type;
            const groupSx = newGroup ? { borderTop: 2, borderTopColor: "divider" } : null;
            return (
              <Box key={row.name} sx={{ display: "contents" }}>
                <Box sx={{
                  px: 0.75, py: 0.6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  borderBottom: 1, borderColor: "divider", ...groupSx,
                }}>
                  <Typography component="span" sx={{ fontSize: FS.CAPTION, color: "text.primary" }}>{row.name}</Typography>
                  {row.type && (
                    <Typography component="span" sx={{ fontSize: FS.CAPTION, color: "text.secondary", ml: 0.5 }}>
                      {row.type}
                    </Typography>
                  )}
                </Box>
                {items.map((it, ii) => {
                  const net = byKey.get(`${li}|${ii}`);
                  return (
                    <Tooltip key={it} title={net == null ? "" : `${row.name} · ${it}: ${fmtVal(net)}`} arrow>
                      <Box sx={{
                        px: 0.5, py: 0.6, textAlign: "right", borderBottom: 1, borderColor: "divider",
                        bgcolor: cellBg(theme, net, max),
                        color: net == null ? "text.disabled" : "text.primary", ...groupSx,
                      }}>{net == null ? "" : fmtVal(net)}</Box>
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 불균형 상위 — 비율/수량 토글 (명세 §3)
// ─────────────────────────────────────────
/*
 * 🚨 두 정렬은 **완전히 다른 순위**를 낸다 — 비율 1위는 목표재고가 작은 조합이고, 수량 1위는
 *   이탈률이 평범한 큰 거점이다. 그래서 토글로 둘 다 주고, **목표재고 컬럼을 항상 같이** 보여줘
 *   분모를 확인할 수 있게 한다(명세 §3).
 */
const TopImbalanceCard = ({ title, rows, sortKey, onChangeSort, topN, onChangeTopN, unit, fmtVal, grouped, drill, onRowDrill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isAmt = unit === "AMT";
  // 단위에 따라 값·정렬 필드가 통째로 갈린다(수량 net/abs/devPct ↔ 금액 netAmt/absAmt/devAmtPct)
  //   ★ 매 렌더 새 객체를 만들면 아래 useMemo 의존성이 계속 바뀐다 → useMemo 로 고정
  const f = useMemo(() => (isAmt
    ? { net: "netAmt", abs: "absAmt", dev: "devAmtPct", target: "targetAmt" }
    : { net: "net", abs: "abs", dev: "devPct", target: "target" }), [isAmt]);

  const shown = useMemo(() => {
    const key = sortKey === "ratio" ? f.dev : f.abs;
    const sorted = [...(rows ?? [])].sort((a, b) => (b[key] ?? 0) - (a[key] ?? 0));
    return sorted.slice(0, topN);
  }, [rows, sortKey, topN, f.dev, f.abs]);

  const columnDefs = useMemo(() => [
    { headerName: t("location"), field: "loc", flex: 1, minWidth: 96 },
    { headerName: t("itemCd"), field: "itemCd", flex: 1, minWidth: 100 },
    // ABC-XYZ 는 우선순위 판단에 쓴다(A급 불균형과 C급 불균형은 급한 정도가 다르다).
    //   계층 롤업 중에는 서버가 null 을 주므로 컬럼 자체를 뺀다 — 그룹에선 등급이 뜻이 없다.
    ...(grouped ? [] : [{ headerName: t("invColAbc"), field: "abc", width: 68, cellRenderer: AbcXyzChip }]),
    { headerName: t("psiNet"), field: f.net, width: 88, type: "rightAligned",
      valueFormatter: (p) => fmtVal(p.value ?? 0),
      // 방향이 한눈에 보여야 한다 — 과잉/부족을 매트릭스와 **같은 색조**로 맞춘다
      cellStyle: (p) => ({
        color: (p.value ?? 0) > 0 ? theme.palette.warning.dark : theme.palette.error.dark,
        fontWeight: 700, textAlign: "right",
      }) },
    { headerName: t("psiAbs"), field: f.abs, width: 84, type: "rightAligned",
      valueFormatter: (p) => fmtVal(p.value ?? 0) },
    { headerName: t("psiDevPct"), field: f.dev, width: 78, type: "rightAligned",
      valueFormatter: (p) => (p.value == null ? "-" : `${p.value}%`) },
    { headerName: isAmt ? t("psiTargetAmt") : t("psiTargetQty"), field: f.target, width: 88, type: "rightAligned",
      valueFormatter: (p) => fmtVal(p.value ?? 0) },
  ], [t, theme, f, isAmt, grouped, fmtVal]);

  const topNOptions = Array.from({ length: MAX_TOP_N }, (_, i) => ({ value: i + 1, label: `Top ${i + 1}` }));

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>
        {/* SegToggle 은 옵션 값을 t(값.toLowerCase()) 로 번역한다 — 여기 라벨은 페이지 전용 키라 Select 를 쓴다 */}
        {/* 정렬 축은 단위와 독립이다 — "크기순"이냐 "비율순"이냐만 고르고, 수량/금액은 상단 토글이 정한다 */}
        <CompactSelect value={sortKey} minWidth={92} onChange={onChangeSort}
          options={[{ value: "size", label: t("psiByQty") }, { value: "ratio", label: t("psiByRatio") }]} />
        <CompactSelect value={topN} options={topNOptions} onChange={onChangeTopN} minWidth={78} />
      </DashboardCardHeader>
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme, !!onRowDrill)}>
        <AgGridReact
          theme="legacy"
          rowData={shown}
          columnDefs={columnDefs}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          headerHeight={32}
          suppressCellFocus
          /* 행 → 그 거점의 PSI 시계열. 권한이 없으면 핸들러가 안 와서 커서도 안 바뀐다 */
          onRowClicked={onRowDrill ? (e) => onRowDrill(e.data) : undefined}
        />
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 재배치 기회 — 품목별 과잉↔부족 (명세 §4)
// ─────────────────────────────────────────
const TransshipRow = ({ row, expanded, onToggle, unit, fmtVal }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
      <Box onClick={onToggle} sx={{
        display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.8, cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
      }}>
        <ExpandMoreIcon sx={{
          fontSize: 16, color: "text.secondary",
          transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s",
        }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: FS.BODY, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.itemNm || row.itemCd}
          </Typography>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
            {t("excess")} {row.overLocs} · {t("shortage")} {row.shortLocs}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography sx={{ fontSize: FS.SUB, fontWeight: 700, color: "primary.main" }}>{fmtQty(row.movable)}</Typography>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("psiMovable")}</Typography>
        </Box>
      </Box>
      {/* 펼치면 "어디서 어디로" — 과잉 거점 → 부족 거점 순 */}
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1, pb: 1 }}>
          {(row.byLoc ?? []).map((l) => (
            <Box key={l.loc} sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
              <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {l.loc}{l.locType ? ` (${l.locType})` : ""}
              </Typography>
              <Typography sx={{
                fontSize: FS.CAPTION, fontWeight: 600, flexShrink: 0, ml: 1,
                color: l.net > 0 ? theme.palette.warning.dark : theme.palette.error.dark,
              }}>{fmtVal(unit === "AMT" ? l.netAmt : l.net)}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const TransshipCard = ({ title, rows, unit, fmtVal, drill }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState({});
  const list = rows ?? [];
  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {list.length === 0 && (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("noData")}</Typography>
          </Box>
        )}
        {list.map((r) => (
          <TransshipRow key={r.itemCd} row={r} expanded={!!open[r.itemCd]} unit={unit} fmtVal={fmtVal}
            onToggle={() => setOpen((p) => ({ ...p, [r.itemCd]: !p[r.itemCd] }))} />
        ))}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const PsiBalanceDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevel ?? "");
  const [unit, setUnit] = useState(reduxViewState?.unit ?? "QTY");            // 수량/금액 — 페이지 전체 공통
  // 정렬 축: size(크기순) | ratio(비율순). 옛 저장값("abs"/"devPct")은 size/ratio 로 접어 읽는다
  const [sortKey, setSortKey] = useState(
    reduxViewState?.psiSort === "devPct" || reduxViewState?.psiSort === "ratio" ? "ratio" : "size");
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [drillHoverKey, setDrillHoverKey] = useState(null);

  const { payload } = useDashboardData(psiBalanceService.get, { itemLevel: itemLevel || undefined });
  const { topN, saveTopN, layout, saveLayout } =
    useDashboardP13n(viewName, { defaultLayout: DEFAULT_LAYOUT });

  useEffect(() => {
    const next = reduxUtil.createObjectState(viewName, { itemLevel, unit, psiSort: sortKey });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemLevel, unit, sortKey]);

  const cardLabels = useMemo(() => ({
    matrix: t("psiMatrixCard"),
    topImbalance: t("psiTopImbalanceCard"),
    transshipment: t("psiTransshipCard"),
  }), [t]);

  if (!payload) return null;

  const kpis = payload.kpis ?? {};
  const imbalance = kpis.imbalance ?? {};
  const isAmt = unit === "AMT";
  const fmtMoney = makeFmtMoney(payload.currencyCd);
  const fmtVal = isAmt ? fmtMoney : fmtQty;
  const grouped = !!payload.itemGrouped;

  /*
   * 거점 유형 분해 — "DC 58 · STO 22" 처럼 KPI sub 에 붙인다.
   *   mj 에서는 **부족이 전부 STO** 라 이 한 줄이 "창고에 쌓이고 매장이 굶는다" 를 말해 준다.
   *   0 인 유형도 지우지 않는다 — "DC 부족 0" 이 곧 메시지다.
   */
  const typeText = (byType) => (byType ?? [])
    .map((b) => `${b.type || t("others")} ${b.combos}`)
    .join(" · ");

  const levelOptions = (payload.itemLevels ?? []).map((lv) => ({
    value: String(lv.value),
    label: lv.label || lv.cd || `${t("level")} ${lv.value}`,
  }));

  /*
   * 드릴 — PSI 흐름(시계열)은 이 화면이 안 그린다. 명세 §8 대로 Location Inventory 로 보낸다.
   *
   * 제목·KPI 는 **조건 없이** 연다 — 카드 전체가 전사 집계라 좁힐 기준이 없다.
   */
  const openLocationInventory = () => {
    reduxDispatch(reduxUtil.updateHistoryState(DRILL.locationInventory));
  };

  /*
   * 불균형 **행** → 그 거점의 PSI 시계열.
   *
   * ⚠️ **거점까지만 좁혀진다 — 품목은 안 넘어간다.** 행은 거점 × 품목인데 대상 화면에는
   *   품목을 고르는 자리가 없다(거점 하나씩 보는 화면이고, 품목 축은 예외 테이블에 표시만 된다).
   *   그래도 거는 이유: 지금은 아무것도 안 심어 **전사 전체**가 열린다. 거점이라도 짚어 주는 편이
   *   명세 §8 의 의도("시계열이 필요하면 05 로")에 가깝다.
   *   → Inventory 의 롤업 행과는 다른 상황이다. 거기는 품목코드가 **없어서** 못 심었고,
   *     여기는 코드가 있는데 **받을 자리가 없다**.
   *
   * ★ 거점은 **이름**으로 심는다 — 대상이 `r.loc === selectedLoc` 으로 매칭하기 때문이다(코드가 아니다).
   *   같은 DW 거점명을 쓰므로 값은 일치한다.
   */
  const openLocationInventoryRow = (row) => {
    if (!row?.loc) return;
    reduxDispatch(reduxUtil.updateViewState(
      reduxUtil.createObjectState(DRILL.locationInventory, { selectedLoc: row.loc })));
    reduxDispatch(reduxUtil.updateHistoryState(DRILL.locationInventory));
  };
  const drillIf = (key, fn) => (isPermitted(key) ? fn : undefined);
  const titleDrill = (cardKey) => {
    const fn = drillIf(DRILL.locationInventory, openLocationInventory);
    return fn && {
      onDrill: fn,
      drillTitle: t("locationInventoryStatus"),
      onDrillHover: (on) => setDrillHoverKey(on ? cardKey : null),
    };
  };

  const renderCard = (key, drill) => {
    switch (key) {
      case "matrix":
        return <MatrixCard title={cardLabels.matrix} matrix={payload.matrix}
          unit={unit} fmtVal={fmtVal} drill={drill} />;
      case "topImbalance":
        return <TopImbalanceCard title={cardLabels.topImbalance} rows={payload.topImbalance}
          sortKey={sortKey} onChangeSort={setSortKey} topN={topN} onChangeTopN={saveTopN}
          unit={unit} fmtVal={fmtVal} grouped={grouped} drill={drill}
          onRowDrill={drillIf(DRILL.locationInventory, openLocationInventoryRow)} />;
      case "transshipment":
        return <TransshipCard title={cardLabels.transshipment} rows={payload.transshipment}
          unit={unit} fmtVal={fmtVal} drill={drill} />;
      default:
        return null;
    }
  };

  const spanOf = (key) => CARD_DEFS.find((c) => c.key === key)?.span ?? 3;
  const minHOf = (key) => CARD_DEFS.find((c) => c.key === key)?.minH ?? 260;
  const visibleOrder = (layout?.order ?? DEFAULT_LAYOUT.order).filter((k) => !layout?.hidden?.[k]);

  const gridRows = visibleOrder.reduce((rows, key) => {
    const s = spanOf(key);
    const last = rows[rows.length - 1];
    if (!last || last.used + s > 6) rows.push({ keys: [key], used: s });
    else { last.keys.push(key); last.used += s; }
    return rows;
  }, []);
  const gridTemplateRows = gridRows
    .map((r) => { const m = Math.max(...r.keys.map(minHOf)); return `minmax(${m}px, ${m}fr)`; })
    .join(" ");

  return (
    <DashboardPage>
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        {levelOptions.length > 1 && (
          <CompactSelect displayEmpty value={itemLevel} options={[{ value: "", label: t("itemLevel") }, ...levelOptions]}
            onChange={setItemLevel} minWidth={96} />
        )}
        {/* 단위 토글은 **페이지에 하나** — KPI·매트릭스·상위·재배치를 한꺼번에 바꾼다(스킬 §1) */}
        <SegToggle value={unit} options={["AMT", "QTY"]} onChange={setUnit} />
        <IconButton size="small" onClick={() => setLayoutOpen(true)} sx={{ p: 0.4, color: "text.secondary" }}>
          <TuneIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </DashboardTopBar>

      {/*
        KPI 4장 — 이 화면은 **직전 구간 비교가 없다**(계획 지평 누적 단일값, 명세 §0-1).
        그래서 deltaPct 를 넘기지 않는다 → KpiCard 가 증감 영역을 아예 그리지 않는다(스킬 §주의).
        KPI 안에 클릭 요소가 없으므로 카드 전체가 드릴 링크(스킬 §9-1).
      */}
      <DashboardRow kpi columns={4}>
        {/* 🚨 대표 지표는 **절대 이탈률**이다. sub 의 순 이탈률과 배수가 "상쇄가 얼마나 가리는가" 를 말한다 */}
        <KpiCard label={t("psiImbalanceIndex")}
          value={`${(isAmt ? imbalance.absAmtPct : imbalance.absPct) ?? 0}%`}
          sub={imbalance.hiddenMultiple
            ? `${t("psiNetPct")} ${(isAmt ? imbalance.netAmtPct : imbalance.netPct) ?? 0}% · ${t("psiHidden")} ${imbalance.hiddenMultiple}x`
            : `${t("psiNetPct")} ${(isAmt ? imbalance.netAmtPct : imbalance.netPct) ?? 0}%`}
          onClick={drillIf(DRILL.locationInventory, openLocationInventory)} />
        {/* sub = 거점 유형 분해. 총계보다 이쪽이 행동을 정한다(어디가 쌓이고 어디가 굶는가) */}
        <KpiCard label={t("psiOverCombos")} value={`${kpis.over?.combos ?? 0}`}
          sub={`${fmtVal(isAmt ? (kpis.over?.amt ?? 0) : (kpis.over?.qty ?? 0))} · ${typeText(kpis.over?.byType)}`}
          onClick={drillIf(DRILL.locationInventory, openLocationInventory)} />
        <KpiCard label={t("psiShortCombos")} value={`${kpis.short?.combos ?? 0}`}
          sub={`${fmtVal(isAmt ? (kpis.short?.amt ?? 0) : (kpis.short?.qty ?? 0))} · ${typeText(kpis.short?.byType)}`}
          onClick={drillIf(DRILL.locationInventory, openLocationInventory)} />
        <KpiCard label={t("psiTransshipItems")} value={`${kpis.transship?.items ?? 0}`}
          sub={`${t("psiMovable")} ${fmtQty(kpis.transship?.movableQty ?? 0)}`}
          onClick={drillIf(DRILL.locationInventory, openLocationInventory)} />
      </DashboardRow>

      <Box sx={{
        flex: 1, minHeight: 0, overflow: "auto",
        display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows,
        gridAutoFlow: "row dense", gap: 1.5,
      }}>
        {visibleOrder.map((key) => {
          const drill = titleDrill(key);
          return (
            <Card key={key}
              sx={{
                gridColumn: `span ${spanOf(key)}`, minHeight: 0,
                transition: CARD_HOVER_TRANSITION,
                ...(drillHoverKey === key ? CARD_HOVER_SX : {}),
              }}>
              {renderCard(key, drill)}
            </Card>
          );
        })}
      </Box>

      <DashboardLayoutModal
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        cards={CARD_DEFS.map((c) => ({ key: c.key, label: cardLabels[c.key] }))}
        layout={layout ?? DEFAULT_LAYOUT}
        onSave={saveLayout}
      />
    </DashboardPage>
  );
};

export default PsiBalanceDashboard;
